import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  setAuthSession,
  clearAuthSession,
} from '../utils/tokenStorage';
import { queryClient } from '../providers/query-client';
import { isTokenExpiredOrExpiringSoon } from '../utils/jwtUtils';

const baseURL =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:7000'
    : 'https://erpdevapi.humanrights.or.tz');
const authBypassEnabled = String(process.env.REACT_APP_AUTH_BYPASS).toLowerCase() === 'true';

export const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let refreshQueue = [];

const redirectToLogin = () => {
  if (typeof window !== 'undefined') {
    // Clear all authentication data
    clearAuthSession();
    
    // Clear React Query cache to remove any cached user data
    if (queryClient) {
      queryClient.clear();
    }
    
    // Redirect to login page (HashRouter uses #/)
    const currentHash = window.location.hash;
    // Only redirect if not already on login page
    if (currentHash !== '#/' && currentHash !== '') {
      window.location.replace('/#/');
    } else if (!currentHash && window.location.pathname !== '/') {
      window.location.replace('/');
    }
  }
};

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  refreshQueue = [];
};

/**
 * Refresh tokens silently. Call when access token is expired or about to expire.
 * Response: { success, message, data: { accessToken, refreshToken } }
 * @returns {Promise<boolean>} - true if refreshed, false otherwise
 */
export const refreshTokensSilently = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const refreshResponse = await refreshClient.post(
      '/api/auth/refresh-token',
      { refreshToken },
      { __isRefreshRequest: true }
    );

    const data = refreshResponse?.data?.data;
    if (!data?.accessToken) return false;

    const user = getStoredUser();
    setAuthSession({ accessToken: data.accessToken, refreshToken: data.refreshToken, user });
    return true;
  } catch {
    return false;
  }
};

/** Check if access token needs refresh (expired or expiring within buffer). Buffer: 60 seconds. */
export const needsTokenRefresh = (bufferMs = 60000) => {
  const accessToken = getAccessToken();
  if (!accessToken) return false;
  return isTokenExpiredOrExpiringSoon(accessToken, bufferMs);
};

apiClient.interceptors.request.use(
  async (config) => {
    // Never attempt to refresh while performing refresh itself.
    if (config?.__isRefreshRequest) return config;

    let accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    // Skip auth for logout endpoint if token is missing/expired
    const isLogoutRequest = config.url?.includes('/api/auth/logout');

    // If access token is missing/expired but refresh token exists, refresh before sending request.
    // This avoids requests going out without Authorization (which triggers "not logged in" in RBAC middleware).
    if ((!accessToken || needsTokenRefresh()) && refreshToken) {
      const ok = await refreshTokensSilently();
      if (ok) {
        accessToken = getAccessToken();
      }
    }

    if (accessToken) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else if (!isLogoutRequest) {
      // For non-logout requests, if there's no token, we'll let the 401 handler deal with it
    }

    // Optional debug logging for workflow actions.
    // Enable by adding `?debugWorkflowActions=1` to the URL (in the browser).
    try {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search || '');
        const debug =
          params.get('debugWorkflowActions') === '1' ||
          params.get('debugWorkflowActions') === 'true';
        const url = String(config?.url ?? '');
        const isWorkflowAction =
          url.includes('/api/workflow/workflows/') && url.endsWith('/actions') && String(config?.method ?? '').toLowerCase() === 'post';
        if (debug && isWorkflowAction) {
          // eslint-disable-next-line no-console
          console.log('[apiClient debug] POST workflow action', {
            url,
            data: config?.data,
            headers: {
              'Content-Type': config?.headers?.['Content-Type'] ?? config?.headers?.get?.('Content-Type'),
            },
          });
        }
      }
    } catch (_) {
      // no-op
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Template mode: never redirect to login automatically.
    if (authBypassEnabled) {
      return Promise.reject(error);
    }

    const { response, config } = error || {};
    const status = response?.status;

    // Never redirect or refresh when the failing request was login, change-password, or verify-otp - let the form show the error
    const isLoginRequest = config?.url?.includes?.('/api/auth/login') === true;
    const isChangePasswordRequest = config?.url?.includes?.('/api/auth/change-password') === true;
    const isVerifyOtpRequest = config?.url?.includes?.('/api/auth/verify-otp') === true;
    if (status === 401 && (isLoginRequest || isChangePasswordRequest || isVerifyOtpRequest)) {
      return Promise.reject(error);
    }

    if (status === 401 && config && !config._retry && !config.__isRefreshRequest) {
      config._retry = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearAuthSession();
        if (queryClient) {
          queryClient.clear();
        }
        redirectToLogin();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((newAccessToken) => {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiClient(config);
          })
          .catch((queueError) => Promise.reject(queueError));
      }

      isRefreshing = true;

      try {
        const refreshResponse = await refreshClient.post(
          '/api/auth/refresh-token',
          { refreshToken },
          { __isRefreshRequest: true }
        );

        const tokens = refreshResponse?.data?.data;

        if (!tokens?.accessToken) {
          throw new Error('Unable to refresh authentication tokens');
        }

        const user = getStoredUser();
        setAuthSession({ ...tokens, user });
        processQueue(null, tokens.accessToken);

        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        return apiClient(config);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthSession();
        if (queryClient) {
          queryClient.clear();
        }
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle any other 401 errors (not handled above)
    // Do not redirect when login, change-password, or verify-otp failed - let the form show the error message
    if (response?.status === 401) {
      const isLoginRequest = config?.url?.includes('/api/auth/login');
      const isChangePasswordRequest = config?.url?.includes('/api/auth/change-password');
      const isVerifyOtpRequest = config?.url?.includes('/api/auth/verify-otp');
      const isLogoutRequest = config?.url?.includes('/api/auth/logout');

      if (!isLoginRequest && !isChangePasswordRequest && !isVerifyOtpRequest && !isLogoutRequest) {
        clearAuthSession();
        if (queryClient) {
          queryClient.clear();
        }
        redirectToLogin();
      }
    }

    // Handle 403 Forbidden errors that require re-authentication
    if (response?.status === 403) {
      const errorMessage = response?.data?.message || '';
      // If the error indicates authentication is required, clear session
      if (errorMessage.toLowerCase().includes('not logged in') || 
          errorMessage.toLowerCase().includes('authentication') ||
          errorMessage.toLowerCase().includes('token')) {
        clearAuthSession();
        if (queryClient) {
          queryClient.clear();
        }
        redirectToLogin();
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
